import {
  getSessionItem,
  setSessionItem,
} from '@/core/services/session-storage/session-storage';

export const ACCOUNT_LAST_PATH_STORAGE_KEY = 'mindoasisAccountLastPath';
export const ACCOUNT_PREVIOUS_PATH_STORAGE_KEY =
  'mindoasisAccountPreviousPath';

const EXCLUDED_PATH_PREFIXES = [
  '/account/lookup',
  '/account/signin',
  '/account/signup',
  '/account/reset-password',
  '/account/set-password',
] as const;

/**
 * Paths the account back button may return to: any page except the steps of the
 * authentication flow, which would drop the user back into a flow they left.
 */
export function isAccountReturnPath(pathname: string): boolean {
  if (!pathname.startsWith('/')) {
    return false;
  }

  return !EXCLUDED_PATH_PREFIXES.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function readStoredPath(key: string): string | null {
  const path = getSessionItem(key);
  if (!path || !isAccountReturnPath(path)) {
    return null;
  }
  return path;
}

/**
 * Keeps the two most recently visited eligible paths, so the back button has a
 * destination left even when the user is standing on the most recent one.
 */
export function rememberAccountPath(pathname: string): void {
  if (!isAccountReturnPath(pathname)) {
    return;
  }

  const lastPath = getSessionItem(ACCOUNT_LAST_PATH_STORAGE_KEY);
  if (lastPath === pathname) {
    return;
  }

  if (lastPath) {
    setSessionItem(ACCOUNT_PREVIOUS_PATH_STORAGE_KEY, lastPath);
  }
  setSessionItem(ACCOUNT_LAST_PATH_STORAGE_KEY, pathname);
}

/** Resolved href for the account header back button. */
export function resolveAccountBackHref(currentPathname: string): string {
  const candidates = [
    readStoredPath(ACCOUNT_LAST_PATH_STORAGE_KEY),
    readStoredPath(ACCOUNT_PREVIOUS_PATH_STORAGE_KEY),
  ];

  return candidates.find(path => path && path !== currentPathname) ?? '/';
}
